import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import VisuallyHidden from "../visually-hidden"

// =============================================================================
// Props
// =============================================================================

export type NavigationDockPrimaryActionProps = {
  icon: IconName
  label: string
  /** Swap the rendered element — e.g. pass `<Link href="..." />` for routing. */
  render?: useRender.RenderProp
}

// =============================================================================
// Styles
// =============================================================================

const primaryActionStyles = [
  "pointer-events-auto",
  "flex items-center justify-center shrink-0",
  "h-12 w-12 rounded-full",
  "bg-gray-900 text-white hover:text-white hover:bg-gray-800",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-white",
  "transition-colors"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * A prominent call-to-action button in the dock — rendered as a filled black circle.
 * Typically used for the primary creation action (e.g. "Start writing").
 */
export function NavigationDockPrimaryAction({ icon, label, render }: NavigationDockPrimaryActionProps) {
  return useRender({
    defaultTagName: "button",
    render,
    props: {
      type: "button" as const,
      "data-slot": "navigation-dock-primary-action",
      className: primaryActionStyles,
      children: (
        <>
          <Icon name={icon} aria-hidden />
          <VisuallyHidden>{label}</VisuallyHidden>
        </>
      )
    }
  })
}

NavigationDockPrimaryAction.displayName = "Navigation.Dock.PrimaryAction"
